import { Pencil, Trash2Icon } from "lucide-react"
import { Header } from "../Components/Header/Header"
import { ColumnDef, DataTable } from "../Components/Table/DataTable"
import useGetCustomData from "../Hooks/useGetCustomData"
import useGetData from "../Hooks/useGetData"
import { AddButton, BulkImportLink, CancelButton, CloseButton, IconActionButton, ModalHeader, ModalOverlay, PageContainer, SubmitButton } from "../Styles/AppStyles"
import { BinLocation, USER_ROLE } from "../Types"
import { useEffect, useState } from "react"
import { LocationSelect } from "../Components/Select/LocationSelect"
import BinForm, { BinFormInput } from "./Bin/BinForm"
import BinBulkImport from "./Bin/BinBulkImport"
import { ModalContent } from "./UsersPage"
import apiClient from "../Services/ApiClient"
import { useSnackbar } from "../Context/SnackbarProvider"
import { decode } from "../Services/DecodeToken"
import { Box, Modal, Typography } from "@mui/material"




export const LocationBinPage = () => {

    const [bins, loading, error, refetch] = useGetData<BinLocation>('/api/bins');

    const [binToEdit, setBinToEdit] = useState<any | null>(null);
    const [binToDelete, setBinToDelete] = useState<BinLocation | null>(null);

    const [showAddModal, setShowAddModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    const handleCloseModal = () => {
    setShowAddModal(false);
    setBinToEdit(null);
  };

    const [ovs] = useGetCustomData('/api/location/user/ovs');
    const [selectedOvs, setSelectedOvs] = useState<string>('');

    const token: any = decode();


    const [filteredData, setFilteredData] = useState<BinLocation[]>();


      const {showSnackbar} = useSnackbar();
    

       useEffect(() => {
        if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && ovs.length > 0 && !selectedOvs) {
          setSelectedOvs(ovs[0].OVID);
        }
      }, [token.roles.id, ovs, selectedOvs]);

    useEffect(() => {
        const filtered = selectedOvs
            ? bins.filter(bin => bin.vending_location_id === Number(selectedOvs))
            : bins;

        setFilteredData(filtered);
    }, [bins, selectedOvs]);


     // Pulls the API's error message out of the response so validation (400)
     // and duplicate (409) failures are shown verbatim instead of a generic one.
     const readErrorMessage = async (response: Response, fallback: string): Promise<string> => {
        try {
            const body = await response.json();
            return body?.error || body?.message || fallback;
        } catch {
            return fallback;
        }
     };

     const handleSubmit = async (bin: BinFormInput) => {

        // bin_location_name is intentionally not sent — the API derives it from
        // ailse/rack/shelf/bin.
        const payload = {
            ailse: bin.ailse,
            rack: bin.rack,
            shelf: bin.shelf,
            bin: bin.bin,
            vending_location_id: Number(bin.ovid),
            customer_id: Number(bin.customer_id),
        }


        try {
            const response = binToEdit
                ? await apiClient(`/api/bins/${binToEdit.bin_location_id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                })
                : await apiClient('/api/bins', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

            if (!response.ok) {
                showSnackbar(await readErrorMessage(response, 'Failed to save bin location'), 'error');
                return;
            }

            showSnackbar('Bin location saved successfully', 'success');
            refetch();
            handleCloseModal();
        }
        catch (error) {
            console.error('Error submitting bin location:', error);
            showSnackbar('Failed to save bin location. Please try again.', 'error');
        }

     };

     const [showDeleteModal, setShowDeleteModal] = useState(false);


     const handleDelete = async (binId: number) => {

     }



      const binLocationColumns: ColumnDef<any>[] = [
        {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
          renderCell: (params) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: "center", marginTop: 12 }}>
      <IconActionButton onClick={() => {
        setBinToEdit(params.row);
        setShowAddModal(true);
      }} title="Edit">
        <Pencil />
      </IconActionButton>

      <IconActionButton
        onClick={() => {
          setBinToDelete(params.row);
          setShowDeleteModal(true);
        }}
        $variant="danger"
        title="Delete"
      >
        <Trash2Icon />
      </IconActionButton>
    </div>
  )
    },
    { field: 'bin_location_name', headerName: 'Bin Name', width: 200, searchable: true },
    { field: 'ailse', headerName: 'Aisle', width: 120, searchable: true },
    { field: 'rack', headerName: 'Rack', width: 100 },
    { field: 'shelf', headerName: 'Shelf', width: 100 },
    { field: 'bin', headerName: 'Bin', width: 100 },
    { field: 'vending_location_name', headerName: 'Location', width: 200, searchable: true },
    { field: 'customer_name', headerName: 'Account', width: 200, searchable: true },
    { field: 'created_date', headerName: 'Created', width: 130,  },
    { field: 'modified_date', headerName: 'Modified', width: 130,  },
];

    

const handleConfirmDelete = async () => {
try {
        const response = await apiClient(`/api/bins/${binToDelete?.bin_location_id}`, {
        method: 'DELETE',
        });

        if (!response.ok) {
        showSnackbar(await readErrorMessage(response, 'Failed to delete bin location'), 'error');
        return;
        }

        showSnackbar('Bin deleted successfully', 'success');
  
    } catch (err) {
        console.error('Delete error:', err);
        showSnackbar('Error deleting bin location', 'error');
    } finally {
        setShowDeleteModal(false);
        setBinToDelete(null);
        refetch();
    }
}

    

    return (
        <PageContainer>
                <Header title="Bin Management" data={filteredData}>
                     <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
                     <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>
                </Header>

                {showImportModal && (
                  <ModalOverlay onClick={() => setShowImportModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                      <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
                      <ModalHeader>Bulk Import</ModalHeader>
                      <BinBulkImport
                        onClose={() => setShowImportModal(false)}
                        onSuccess={() => refetch()}
                      />
                    </ModalContent>
                  </ModalOverlay>
                )}


                   {showAddModal && (
                

                  <Modal open={showAddModal} onClose={() => setShowAddModal(false)} >
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'white',
                            borderRadius: 2,
                            maxWidth: 800,
                            width: 'calc(100% - 32px)',
                            maxHeight: '90dvh',
                            overflow: 'auto',
                            p: 3,
                            boxShadow: 24,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CloseButton onClick={() => setShowAddModal(false)}>x</CloseButton>
                
                          <Typography variant="h6" gutterBottom>
                            
                              <ModalHeader>{binToEdit ? "Edit" : "Add"}  Location Bin</ModalHeader>
                         
                         
                          </Typography>

                           <BinForm ovs={ovs} onSubmit={handleSubmit} defaultValues={binToEdit ?? undefined} onCancel={handleCloseModal} />
                         
                           
                        
                        </Box>
                      </Modal>

      )}


      {showDeleteModal && binToDelete && (
  <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        borderRadius: 2,
        padding: 4,
        maxWidth: 400,
        width: '90%',
        boxShadow: 24,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="h6" gutterBottom>
        Confirm Delete
      </Typography>

      <Typography sx={{ mb: 3 }}>
        Are you sure you want to delete <strong>{binToDelete.bin_location_name}</strong>?
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <SubmitButton
          onClick={handleConfirmDelete}
        >
          Confirm
        </SubmitButton>
        <CancelButton onClick={() => setShowDeleteModal(false)}>Cancel</CancelButton>
      
      </Box>
    </Box>
  </Modal>
)}



                <DataTable
                    columns={binLocationColumns}
                    data={filteredData ?? []}
                    loading={loading}
                    pageKey="bins"
                    defaultSort={[{ field: 'bin_location_name', sort: 'asc' }]}
                    idField="bin_location_id"
                    
                >


                     <LocationSelect
                        ovs={ovs}
                        selectedOvs={selectedOvs}
                        onChange={setSelectedOvs}
                    />

                </DataTable>


                

        </PageContainer>
    )
}